// Workflow
// .d.ts not supported with enums

import { Features } from '../helpers/PermissionUtility-types';
import { TemplateFolderList, TemplateList } from '../types/template-types';

export type Workflow = {
  description?: string;
  id?: string;
  name: string;
  trigger: WorkflowTrigger;
  steps: WorkflowStep[];
  active: boolean;
  clientId?: string;
  lastUpdatedDate?: string;
  deletedDate?: string;
  recipeId?: string;
  folderId?: string;
  linkedResources?: LinkedResource[];
  latestVersion?: WorkflowVersion;
  isDraft?: boolean;
  draft?: WorkflowVersion;
};

export type WorkflowListType = {
  data: {
    count: number;
    totalCount: number;
    items: Workflow[];
  };
  requestId: string;
  statusCode: number;
};

export type WorkflowTrigger = {
  type?: string;
  eventType?: string;
  entityId?: string;
  entityIds?: string[];
  appId?: string;
  appInstanceId?: string;
  environment?: string;
  timeZone?: string;
  cronString?: string;
  userId?: string;
  groupId?: string;
  onlyRunOnInitialCompletion?: boolean;
};

export type LinkedResource = {
  resourceId: string;
  resourceType: number;
};

export type WorkflowStepConfig = Record<string, string> & {
  appInstanceId?: string;
  appId?: string;
  appCommand?: string;
};

export type WorkflowStep = {
  id: string;
  action: {
    adapterName: string;
    friendlyName?: string;
  };
  config: WorkflowStepConfig;
};

export type WorkflowMetadata = {
  steps?: WorkflowMetadataStep[];
};

export type InputConfigItem = Record<string, string>;
export type InputConfig = Record<string, InputConfigItem>;

export type DynamicMetadata = {
  inputs: StepInputMetadata[];
  outputs: StepOutputMetadata[];
};

export type WorkflowMetadataStep = {
  id: string;
  inputConfig?: InputConfig;
  dynamicMetadata?: DynamicMetadata;
};

export type WorkflowVersion = {
  name: string;
  id: string;
  versionNumber: number;
  active: boolean;
  autoDeactivated: boolean;
  createdBy: string;
  createdDate: string;
  createdByName: string;
  steps: WorkflowStep[];
  note: string;
};

export type WorkflowVersionNote = {
  versionId: string;
  note: string;
};

export type WorkflowFolder = {
  id: string;
  name: string;
  parentId?: string;
};

// Apps

export type PublishedApp = {
  appId: string;
  package: Package;
  active: boolean;
  appName: string;
  author: string;
  stamp: string;
  icon: string;
  description: string;
  authInfo: AuthInfo;
  authenticationOptions: AuthenticationOption[];
  configs: AppConfigValue[];
  scopes: string[];
  permissions: AppItemPermission[];
};

export type AuthenticationOption = {
  id: string;
  name: string;
  type: string;
  associatedConfigs: string[];
  isDefault: boolean;
  authInfo: AuthInfo;
};

export type AuthInfo = {
  clientId: string;
  scope: string;
  authorizeUrl: string;
  authorizationCodeUrl: string;
  refreshTokenUrl: string;
};

export type Package = {
  id: string;
  appId: string;
  lastUpdatedDate: string;
  createdDate: string;
  commands: AppCommand[];
  appVersion: string;
  sdkVersion: string;
  assembly: string;
  approved: boolean;
  metadata: AppMetadata;
};

export type AppCommand = {
  icon: string;
  helpUrl: string;
  warningNote: string;
  key: string;
  typeFullName: string;
  name: string;
  externalNotification?: boolean;
};

export type AppConfigValue = {
  key: string;
  description: string;
  configType: string;
  defaultValue: string;
  required: boolean;
  secret: boolean;
};

export type AppItemPermission = {
  name: string;
  type: AppItemPermissionType;
  level: AppItemPermissionLevel;
};

export enum AppItemPermissionType {
  Command,
  Webhook,
  Event
}

export enum AppItemPermissionLevel {
  Team = 'team',
  Advanced = 'advanced',
  Enterprise = 'enterprise',
  AddOn = 'addon',
  Unlisted = 'unlisted',
  Deprecated = 'deprecated'
}

export type InputType = 'text' | 'objectList' | 'bool' | 'int' | 'fileList';

export type StepInputMetadata = {
  key: string;
  alternateKeys?: string[];
  label: string;
  editor?: string;
  helpText?: string;
  warningNote?: string;
  source?: string;
  sourceFilter?: string;
  values?: StepInputMetadataValue[];
  placeholder?: string;
  required?: boolean;
  validators?: StepInputValidator[];
  type: InputType;
  properties?: StepInputMetadataProperty[];
  triggersDynamicMetadata?: boolean;
  advanced?: boolean;
  hidden?: boolean;
  doNotOpenDropdown?: boolean;
  jsonSchema?: string;
  tags?: string[];
};

export type StepInputMetadataProperty = {
  key: string;
  label: string;
  editor?: string;
  helpText?: string;
  source?: string;
  sourceFilter?: string;
  values?: StepInputMetadataValue[];
  placeholder?: string;
  required?: boolean;
  type: InputType;
  doNotOpenDropdown?: boolean;
  triggersDynamicMetadata?: boolean;
  tags?: string[];
};

export type StepInputMetadataValue = { label: string; value: string };

export type StepInputValidator = { feature: Features; function: string; validationFailedMessage: string };

export type StepOutputMetadata = {
  key: string;
  type: string;
  label?: string;
  helpText?: string;
  hidden?: boolean;
  tags?: string[];
};

export type WebhookMetadata = {
  appPath: string;
  description: string;
  emittedEvents: string[];
  httpMethod: string;
  id: string;
  name: string;
  path: string;
  workflowTrigger: false;
  outputs?: StepOutputMetadata[];
};

export type EventMetadataValue = { key: string; type: string };

export type EventMetadata = {
  type: string;
  name: string;
  description: string;
  headers: EventMetadataValue[];
  values: EventMetadataValue[];
};

export type StepMetadata = {
  displayName?: string;
  inputs?: StepInputMetadata[];
  outputs?: StepOutputMetadata[];
  adapterName?: string;
  webhooks?: WebhookMetadata[];
  events?: EventMetadata[];
};

export type CommandMetadata = {
  name: string;
  displayName: string;
  description: string;
  dynamicMetadata: boolean;
  inputs: StepInputMetadata[];
  outputs: StepOutputMetadata[];
};

export type AppMetadata = {
  appId: string;
  appName: string;
  appPath: string;
  commands: CommandMetadata[];
  events: EventMetadata[];
  webhooks: WebhookMetadata[];
  globalInstall: boolean;
};

export type ClientAppPermissions = {
  id: string;
  clientId: string;
  appId: string;
  permissions: ClientPermission[];
};

export type ClientPermission = {
  name: string;
};

// Action Types

export type ConfigValue = {
  key: string;
  defaultValue?: string;
  required?: boolean;
};

export interface Action extends ConfigBase {
  adapterName?: string;
  appCommand?: string;
  globalInstall?: boolean;
}

export interface Trigger extends ConfigBase {
  type?: string;
  webhookId?: string;
  eventType?: string;
  environment?: string;
}

export interface ConfigBase {
  id?: string;
  name?: string;
  description?: string;
  appId?: string;
  appName?: string;
  icon?: string;
  config?: ConfigValue[];
  permissionLevel?: AppItemPermissionLevel;
}

export type AvailableActions = {
  actions: Action[];
  triggers: Trigger[];
};

// Misc

export type StepsMetadata = {
  id: string;
  metadata: StepMetadata;
}[];

export type AvailableVariables = {
  steps: { id: string }[];
  knownExpressions: {
    key: string;
    type: string;
    friendlyName?: string;
    description?: string;
    hidden?: boolean;
    stepId: string;
    expression: string;
    alternateExpressions: string[];
    tags: string[];
  }[];
};

export type AppInstance = {
  id: string;
  appId: string;
  appInstanceName: string;
  active: string;
  authenticated: boolean;
  authenticationOptionId: string;
  clientId: string;
  configs: { key: string; value: string }[];
  createdDate: string;
};

export type AppInstancesResult = {
  data: {
    items: AppInstance[];
    count: number;
  };
};

export type PublishedAppsResult = {
  data: {
    items: PublishedApp[];
    count: number;
  };
};

export type ErrorState = Record<string, boolean>;

export type ErrorObj = {
  statusCode: number;
};

export type WorkflowEditorState = {
  isFetching?: boolean;
  isSaving?: boolean;
  isPublishing?: boolean;
  readOnly?: boolean;
  wizardMode?: boolean;
  wizardComplete?: boolean;
  workflow?: Workflow;
  metadata?: WorkflowMetadata;
  templates?: TemplateList;
  templateFolders?: TemplateFolderList;
  isDirty?: boolean;
  selectedTrigger?: boolean;
  selectedStepId?: string;
  errorState?: ErrorState;
  error?: ErrorObj;
  environment?: string;
  nextStepId?: string;
  publishedApps?: PublishedApp[];
  publishedAppsLoaded?: boolean;
  lastSaveDate?: string;
  create?: boolean;
  versionNumber?: number;
};

export type StepFunc = (stepId: string) => void;

export type RecipeResponse = {
  data: { Workflow_Recipes: Recipe[] };
};

export type Recipe = {
  id: string;
  name: string;
  description: string;
  trigger: WorkflowTrigger;
  steps: Step[];
  environment: string[];
  trial?: boolean;
};

export type Step = {
  id: string;
  appId: string;
  appCommand: string;
  config: { [key: string]: string };
};

export enum RecipeFilterType {
  Integration = 'Integrations',
  Tier = 'Tiers'
}

export type UseCaseResponse = {
  data: { Workflow_Usecases: UseCase[] };
};

export type UseCase = {
  id: string;
  App: string;
  Usecases: string;
};
